import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '~/stores/auth/AuthStore'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { performLogout } from '~/services/auth/useLogoutService'
import {
  clearPersistedLastActivity,
  readPersistedLastActivity,
  writePersistedLastActivity,
} from '~/utils/idle-activity'

const PERSIST_INTERVAL_MS = 10_000

export function useIdleDetection() {
  const { authUser, isLoggedIn } = storeToRefs(useAuthStore())
  const route = useRoute()

  const isWarningShown = ref(false)
  const isIdleDetectionActive = ref(false)
  const lastActivity = ref(Date.now())
  let lastPersistedAt = 0

  const warningTimer = ref<ReturnType<typeof setTimeout> | null>(null)
  const logoutTimer = ref<ReturnType<typeof setTimeout> | null>(null)
  const activityCheckTimer = ref<ReturnType<typeof setTimeout> | null>(null)

  const userIdleTimeout = computed(() => {
    const timeoutMinutes = authUser.value?.user_idle_timeout || 30
    return timeoutMinutes * 60 * 1000
  })

  const warningTime = 2 * 60 * 1000 // 2 minutes

  const timeUntilWarning = computed(() => {
    return Math.max(userIdleTimeout.value - warningTime, 60000)
  })

  const activityEvents = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click',
    'keydown',
    'wheel',
  ]

  const excludedRoutes = ['/auth/login', '/auth/forgot-password', '/auth/reset-password', '/sso']

  const shouldBeActive = computed(() => {
    if (!isLoggedIn.value) return false
    if (!authUser.value) return false

    const currentPath = route.path
    const isExcludedRoute = excludedRoutes.some((excludedPath) =>
      currentPath.includes(excludedPath),
    )

    return !isExcludedRoute
  })

  const updateLastActivity = () => {
    lastActivity.value = Date.now()

    // mousemove/scroll fire continuously, so the stamp is only written every
    // PERSIST_INTERVAL_MS. The idle check runs every 5s and the shortest
    // timeout is 15 min, so this cannot shift the logout meaningfully.
    if (lastActivity.value - lastPersistedAt >= PERSIST_INTERVAL_MS) {
      lastPersistedAt = lastActivity.value
      writePersistedLastActivity(authUser.value?.id, lastActivity.value)
    }
  }

  const showWarning = () => {
    if (isWarningShown.value) return

    isWarningShown.value = true

    const event = new CustomEvent('show-idle-warning', {
      detail: {
        countdown: warningTime / 1000,
        onStayLoggedIn: handleStayLoggedIn,
        onLogoutNow: handleLogoutNow,
      },
    })
    window.dispatchEvent(event)

    logoutTimer.value = setTimeout(() => {
      triggerIdleLogout()
    }, warningTime)
  }

  const hideWarning = () => {
    if (!isWarningShown.value) return

    isWarningShown.value = false

    if (logoutTimer.value) {
      clearTimeout(logoutTimer.value)
      logoutTimer.value = null
    }

    const event = new CustomEvent('hide-idle-warning')
    window.dispatchEvent(event)
  }

  const handleStayLoggedIn = () => {
    hideWarning()
    resetIdleTimer()
  }

  const handleLogoutNow = () => {
    triggerIdleLogout()
  }

  const triggerIdleLogout = () => {
    hideWarning()
    stopIdleDetection()
    clearPersistedLastActivity(authUser.value?.id)
    performLogout({ reason: 'idle' })
  }

  const checkIdleState = () => {
    if (!isIdleDetectionActive.value || !shouldBeActive.value) return

    const now = Date.now()
    const timeSinceLastActivity = now - lastActivity.value
    const timeUntilWarningMs = timeUntilWarning.value
    const totalTimeoutMs = userIdleTimeout.value

    if (timeSinceLastActivity >= totalTimeoutMs) {
      triggerIdleLogout()
      return
    }

    if (timeSinceLastActivity >= timeUntilWarningMs && !isWarningShown.value) {
      showWarning()
    }
  }

  const resetIdleTimer = () => {
    updateLastActivity()

    if (warningTimer.value) {
      clearTimeout(warningTimer.value)
      warningTimer.value = null
    }
  }

  const handleActivity = () => {
    if (!isIdleDetectionActive.value) return

    // Don't reset timer during warning - user must click "Stay Logged In"
    if (isWarningShown.value) return

    resetIdleTimer()
  }

  const addActivityListeners = () => {
    activityEvents.forEach((event) => {
      document.addEventListener(
        event as keyof DocumentEventMap,
        handleActivity as EventListener,
        {
          passive: true,
          capture: true,
        } as AddEventListenerOptions,
      )
    })
  }

  const removeActivityListeners = () => {
    activityEvents.forEach((event) => {
      document.removeEventListener(
        event as keyof DocumentEventMap,
        handleActivity as EventListener,
        {
          passive: true,
          capture: true,
        } as EventListenerOptions,
      )
    })
  }

  const startIdleDetection = () => {
    if (!shouldBeActive.value || isIdleDetectionActive.value) return

    isIdleDetectionActive.value = true
    // Resume from the persisted stamp rather than restarting the clock, so a
    // reload can no longer defer the auto-logout indefinitely.
    const persisted = readPersistedLastActivity(authUser.value?.id)
    if (persisted === null) {
      updateLastActivity()
    } else {
      lastActivity.value = persisted
    }
    addActivityListeners()

    activityCheckTimer.value = setInterval(checkIdleState, 5000)
  }

  const stopIdleDetection = () => {
    if (!isIdleDetectionActive.value) return

    isIdleDetectionActive.value = false
    lastPersistedAt = 0
    hideWarning()
    removeActivityListeners()

    if (warningTimer.value) {
      clearTimeout(warningTimer.value)
      warningTimer.value = null
    }
    if (logoutTimer.value) {
      clearTimeout(logoutTimer.value)
      logoutTimer.value = null
    }
    if (activityCheckTimer.value) {
      clearInterval(activityCheckTimer.value)
      activityCheckTimer.value = null
    }
  }

  watch(
    shouldBeActive,
    (active) => {
      if (active) {
        startIdleDetection()
      } else {
        stopIdleDetection()
      }
    },
    { immediate: true },
  )

  watch(
    () => route.path,
    () => {
      if (shouldBeActive.value) {
        resetIdleTimer()
        if (!isIdleDetectionActive.value) {
          startIdleDetection()
        }
      } else {
        stopIdleDetection()
      }
    },
  )

  // Restart idle detection when user changes timeout settings
  watch(userIdleTimeout, (newTimeout, oldTimeout) => {
    if (newTimeout !== oldTimeout && isIdleDetectionActive.value) {
      stopIdleDetection()
      setTimeout(() => {
        startIdleDetection()
      }, 100)
    }
  })

  onMounted(() => {
    if (shouldBeActive.value) {
      startIdleDetection()
    }
  })

  onUnmounted(() => {
    stopIdleDetection()
  })

  return {
    isWarningShown,
    isIdleDetectionActive,
    userIdleTimeout,
    lastActivity,
    startIdleDetection,
    stopIdleDetection,
    resetIdleTimer,
    showWarning,
    hideWarning,
  }
}
